import { Button, Typography } from '@fox/atoms';
import { IBlockProps } from '@fox/blocks';
import { FlexRowSpacedRightAligned } from '@fox/layout';
import { nanoid } from 'nanoid';
import { CREATE_PEOPLE_BLOCK, CREATE_PERSON_BLOCK } from '../../../../form-block-types';
import { EventCapacityStatus, ReservationUpdate, useFormEventContext } from '../../../form-event-context';
import { IField } from '../../../form-root/contracts';
import { FormRenderMode, useFormRuntimeContext } from '../../../form-runtime-context';
import { useFormBlockReRenderer } from '../../../use-form-block-re-renderer';
import { QuestionLabelRender } from '../../question-block/question-label';
import { BUSINESS_NAME_FIELD, CreatePersonBlockMetadata } from '../contracts';
import { getBusinessAddresses } from '../util';
import { BusinessDetailsSection } from './business-details-section';
import { MultiPersonSection } from './multi-person-section';

export const MultiPersonBlock = ({ id, type, props }: IBlockProps<IField>) => {
	const { formRenderMode, getAnswer, answerQuestion } = useFormRuntimeContext();
	useFormBlockReRenderer([props.name]);

	const { event, updateReservationCount } = useFormEventContext();
	const status = event?.status;

	const metaData: CreatePersonBlockMetadata = JSON.parse(props.metadataJson ?? '{}');

	let currentAnswer = getAnswer(props.name) ?? {
		questionName: props?.name ?? 'CreatePerson',
		answer: {
			answerJson: JSON.stringify({
				contacts: [
					{
						id: nanoid()
					}
				],
				businessDetails: {
					allContactsFromBusiness: true
				}
			})
		} as any
	};

	const currentAnswerSelections = JSON.parse(currentAnswer?.answer?.answerJson);

	return (
		<div
			style={{
				display: 'flex',
				flexDirection: 'column',
				gap: 5,
				marginBottom: 20,
				pointerEvents: formRenderMode === FormRenderMode.design ? 'none' : 'auto'
			}}
		>
			{metaData?.businessSection && metaData.editFields?.[BUSINESS_NAME_FIELD] !== undefined ? (
				<BusinessDetailsSection currentAnswer={currentAnswer} id={id} props={props} />
			) : null}
			{formRenderMode !== FormRenderMode.design ? (
				<div style={{ display: 'flex', justifyContent: 'flex-end' }}>
					<Button
						disabled={
							(status === EventCapacityStatus.Available || status === EventCapacityStatus.OnWaitlist) &&
							!event?.reservation &&
							!event?.ticketsEnabled
						}
						startIconName="Add"
						style={{ color: '#272727', minWidth: 'fit-content' }}
						onClick={async () => {
							if (event?.reservation && !event?.ticketsEnabled) {
								const updated = await updateReservationCount({ operation: ReservationUpdate.Add, count: 1 });
								if (!updated) return;
							}

							if (metaData.maximum ? currentAnswerSelections?.contacts?.length < metaData.maximum : true) {
								const buisnessAddresses = getBusinessAddresses(currentAnswerSelections.businessDetails);

								answerQuestion({
									...currentAnswer,
									answer: {
										answerJson: JSON.stringify({
											...currentAnswerSelections,
											contacts: [
												...currentAnswerSelections.contacts,
												currentAnswerSelections.businessDetails?.allContactsFromBusiness
													? {
															id: nanoid(),
															businessName: currentAnswerSelections.businessDetails.businessName,
															...buisnessAddresses
														}
													: { id: nanoid() }
											]
										})
									},
									type: CREATE_PEOPLE_BLOCK
								});
							}
						}}
					>
						Add new contact
					</Button>
				</div>
			) : (
				<></>
			)}
			<QuestionLabelRender style={{ fontSize: '1.25rem', padding: 0 }} value={props.title} id={id} />

			{formRenderMode !== FormRenderMode.design ? (
				currentAnswerSelections?.contacts?.map((contact, i) => {
					return (
						<MultiPersonSection
							currentAnswer={currentAnswer}
							currentAnswerSelections={currentAnswerSelections}
							key={contact.id}
							fieldProps={props}
							id={id}
							renderQuestionLabel={i !== 0}
							type={CREATE_PEOPLE_BLOCK}
							contact={contact}
						/>
					);
				})
			) : (
				<MultiPersonSection
					currentAnswer={currentAnswer}
					currentAnswerSelections={currentAnswerSelections}
					fieldProps={props}
					id={id}
					renderQuestionLabel={false}
					type={CREATE_PERSON_BLOCK}
				/>
			)}
			{currentAnswerSelections?.contacts?.length > 1 ? (
				<FlexRowSpacedRightAligned>
					<Typography>
						{metaData.isRsvp
							? `Total attending: ${currentAnswerSelections.contacts.length}`
							: `Total contacts: ${currentAnswerSelections.contacts.length}`}
					</Typography>
				</FlexRowSpacedRightAligned>
			) : (
				<></>
			)}
		</div>
	);
};
